/**
 * A matrícula do aluno logado: ele já foi liberado, está esperando, ou nem pediu?
 *
 * Só faz sentido para quem é ALUNO puro. Quem alcança professor tem a própria
 * conta de staff e nunca é barrado por isto — inclusive porque toda conta de
 * staff carrega o papel `student` de base (o gatilho de signup dá esse a todo
 * mundo), e ler "tem papel student" como "é aluno" travaria a escola inteira.
 *
 * A leitura é da PRÓPRIA linha, pela policy "Students can view their own roster
 * row". Nenhum service_role aqui: o aluno enxerga a matrícula dele e nada mais.
 *
 * Três estados, e os três precisam de tela diferente:
 *   liberado      → segue a vida
 *   pendente      → modal travado, esperando a escola
 *   sem matrícula → modal pedindo o código da turma (é o caso de quem criou a
 *                   conta e errou o código: a conta existe e a matrícula não)
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { usePapel, alcanca } from './usePapel';

export type EstadoDaMatricula = 'carregando' | 'liberado' | 'pendente' | 'sem_matricula';

export interface Matricula {
  estado: EstadoDaMatricula;
  /** `true` quando este usuário nem deveria passar por esta porta (é staff). */
  ehStaff: boolean;
  /** Nome da turma em que ele está pendente, pra tela dizer qual é. */
  turmaPendente: string | null;
  recarregar: () => void;
}

export function useMatricula(): Matricula {
  const { user, isLoading: carregandoSessao } = useAuth();
  const { papel, carregando: carregandoPapel } = usePapel();
  const [estado, setEstado] = useState<EstadoDaMatricula>('carregando');
  const [turmaPendente, setTurmaPendente] = useState<string | null>(null);
  const [tique, setTique] = useState(0);

  const recarregar = useCallback(() => setTique((t) => t + 1), []);
  const uid = user?.id;
  const ehStaff = alcanca(papel, 'professor');

  useEffect(() => {
    if (carregandoSessao || carregandoPapel) return;
    if (!uid || ehStaff) { setEstado('liberado'); return; }

    let vivo = true;
    supabase
      .from('class_students')
      .select('id, ativo, classes(name)')
      .eq('user_id', uid)
      .then(({ data, error }) => {
        if (!vivo) return;
        if (error) {
          /* Falha de leitura NÃO tranca o aluno: um erro de rede viraria uma
             porta fechada sem motivo, no celular, no meio da aula. Quem decide
             de verdade é o servidor — o `class-room` recusa pendente na entrada
             da sala, e é lá que a trava importa. */
          setEstado('liberado');
          return;
        }
        const linhas = data ?? [];
        if (linhas.some((l) => l.ativo)) { setEstado('liberado'); return; }
        if (linhas.length > 0) {
          const t = linhas[0] as unknown as { classes?: { name?: string } | null };
          setTurmaPendente(t.classes?.name ?? null);
          setEstado('pendente');
          return;
        }
        setEstado('sem_matricula');
      });

    return () => { vivo = false; };
  }, [uid, ehStaff, carregandoSessao, carregandoPapel, tique]);

  return { estado, ehStaff, turmaPendente, recarregar };
}
